import React from 'react';

/**
 * Marca de producto ClinicPilot (manual de identidad v1.0).
 *
 * La marca del producto es ClinicPilot, independiente de la clínica que lo use:
 * estos componentes ponen la marca en el entorno (login, sidebar, pies)
 * sin desplazar la identidad de la clínica.
 *
 * Colores del manual: verde quirófano #2E6E5E · tinta #13231E ·
 * menta brillante #8FD4BC (para «Pilot» sobre fondos oscuros).
 */

/** Isotipo: el «pulso que despega» sobre cuadrado verde redondeado. */
export function ClinicPilotMark({ size = 20, className = '' }) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="ClinicPilot"
    >
      <rect width="100" height="100" rx="22" fill="#2E6E5E" />
      <path d="M16 62h12l8-20 10 34 8-20h8" stroke="#FFF" strokeWidth="8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M62 56 82 36" stroke="#FFF" strokeWidth="8" fill="none" strokeLinecap="round" />
      <path d="M70 34h14v14" stroke="#FFF" strokeWidth="8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Logotipo horizontal: isotipo + wordmark serif («Pilot» siempre en verde/menta). */
export function ClinicPilotLogo({ height = 28, dark = false, className = '' }) {
  return (
    <svg
      viewBox="0 0 470 100"
      height={height}
      className={className}
      role="img"
      aria-label="Logotipo de ClinicPilot"
    >
      <rect width="100" height="100" rx="22" fill="#2E6E5E" />
      <path d="M16 62h12l8-20 10 34 8-20h8" stroke="#FFF" strokeWidth="8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M62 56 82 36" stroke="#FFF" strokeWidth="8" fill="none" strokeLinecap="round" />
      <path d="M70 34h14v14" stroke="#FFF" strokeWidth="8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <text
        x="126"
        y="69"
        fontFamily="Charter, 'Iowan Old Style', Georgia, serif"
        fontSize="58"
        fontWeight="700"
        letterSpacing="-1"
        style={{ fill: dark ? '#FFFFFF' : 'hsl(var(--foreground))' }}
      >
        Clinic<tspan style={{ fill: dark ? '#8FD4BC' : 'hsl(var(--primary))' }}>Pilot</tspan>
      </text>
    </svg>
  );
}

/** Sello «Un producto de ClinicPilot» para pies y esquinas discretas. */
export function ClinicPilotBadge({ dark = false, className = '' }) {
  return (
    <a
      href="https://clinicpilot.es"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium no-underline ${
        dark ? 'text-white/70 hover:text-white' : 'text-muted-foreground hover:text-[#13231E]'
      } ${className}`}
      title="ClinicPilot — Software de gestión clínica a medida"
      data-testid="clinicpilot-badge"
    >
      <ClinicPilotMark size={14} />
      <span>
        Un producto de{' '}
        <span style={{ fontFamily: "Charter, 'Iowan Old Style', Georgia, serif", fontWeight: 700 }}>
          Clinic<span style={{ color: dark ? '#8FD4BC' : '#2E6E5E' }}>Pilot</span>
        </span>
      </span>
    </a>
  );
}
